if (!customElements.get('gift-card-form')) {
  customElements.define(
    'gift-card-form',
    class GiftCardForm extends HTMLElement {
      constructor() {
        super();

        // Elements
        this.form = this.closest('form');
        this.variantIdInput = this.form?.querySelector('[data-variant-id-input]');
        this.variantSelects = this.querySelectorAll('[data-variant-select]');
        this.priceDisplays = document.querySelectorAll('[data-gift-card-price], [data-price-display]');

        // Steps
        this.steps = this.querySelectorAll('[data-step]');
        this.progressStep = this.querySelector('[data-progress-step]');
        this.progressLabel = this.querySelector('[data-progress-label]');
        this.progressFill = this.querySelector('[data-progress-fill]');

        // Purchase type
        this.purchaseTypeWrapper = this.querySelector('[data-purchase-type]');
        this.purchaseOptions = this.querySelectorAll('[data-purchase-option]');

        // Buttons
        this.btnSelectAmount = this.querySelector('[data-btn-select-amount]');
        this.btnContinue = this.querySelector('[data-btn-continue]');
        this.btnAddMyself = this.querySelector('[data-btn-add-myself]');
        this.btnBack = this.querySelector('[data-btn-back]');
        this.btnAddRecipient = this.querySelector('[data-btn-add-recipient]');

        // Form fields
        this.sendToRecipientInput = this.querySelector('[data-send-to-recipient]');
        this.recipientEmail = this.querySelector('[data-recipient-email]');
        this.recipientName = this.querySelector('[data-recipient-name]');
        this.recipientMessage = this.querySelector('[data-recipient-message]');
        this.recipientSendDate = this.querySelector('[data-recipient-send-date]');
        this.messageCount = this.querySelector('[data-message-count]');

        // Error displays
        this.errorEmail = this.querySelector('[data-error-email]');
        this.errorDate = this.querySelector('[data-error-date]');

        // State
        this.currentStep = 1;
        this.buyForMyself = false;
        this.selectedVariantId = null;
        this.selectedVariantPrice = null;

        this.init();
      }

      init() {
        // Set date constraints
        this.setDateConstraints();

        // Bind events
        this.bindVariantSelection();
        this.bindPurchaseType();
        this.bindButtons();
        this.bindFormFields();
        this.bindFormSubmit();

        // Initialize with first available variant
        const firstChecked = this.querySelector('[data-variant-select]:checked');
        if (firstChecked) {
          this.handleVariantChange(firstChecked);
        }
      }

      setDateConstraints() {
        if (!this.recipientSendDate) return;

        const today = new Date();
        const minDate = today.toISOString().split('T')[0];

        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 90);
        const maxDateStr = maxDate.toISOString().split('T')[0];

        this.recipientSendDate.min = minDate;
        this.recipientSendDate.max = maxDateStr;
      }

      bindVariantSelection() {
        this.variantSelects.forEach((input) => {
          input.addEventListener('change', () => this.handleVariantChange(input));
        });
      }

      handleVariantChange(input) {
        this.selectedVariantId = input.value;
        this.selectedVariantPrice = input.dataset.variantPrice;

        // Update hidden variant ID input
        if (this.variantIdInput) {
          this.variantIdInput.value = this.selectedVariantId;
        }

        // Update all price displays
        if (this.selectedVariantPrice) {
          this.priceDisplays.forEach((el) => {
            el.textContent = this.selectedVariantPrice;
          });
        }

        // Show purchase type options
        this.updateStep1UI();
      }

      updateStep1UI() {
        const hasSelection = !!this.selectedVariantId;

        // Toggle select amount button visibility
        if (this.btnSelectAmount) {
          this.btnSelectAmount.classList.toggle('hidden', hasSelection);
        }

        // Toggle purchase type visibility
        if (this.purchaseTypeWrapper) {
          this.purchaseTypeWrapper.classList.toggle('hidden', !hasSelection);
        }

        // Toggle continue/add buttons
        this.updateStep1Buttons();
      }

      updateStep1Buttons() {
        if (this.buyForMyself) {
          this.btnContinue?.classList.add('hidden');
          this.btnAddMyself?.classList.remove('hidden');
        } else {
          this.btnContinue?.classList.remove('hidden');
          this.btnAddMyself?.classList.add('hidden');
        }
      }

      bindPurchaseType() {
        this.purchaseOptions.forEach((option) => {
          option.addEventListener('click', () => {
            const type = option.dataset.purchaseOption;
            this.buyForMyself = type === 'myself';

            // Update UI
            this.purchaseOptions.forEach((opt) => {
              opt.classList.toggle('selected', opt.dataset.purchaseOption === type);
            });

            this.updateStep1Buttons();
            this.updateProgress();
          });
        });
      }

      bindButtons() {
        // Continue to step 2
        this.btnContinue?.addEventListener('click', () => this.goToStep(2));

        // Back to step 1
        this.btnBack?.addEventListener('click', () => this.goToStep(1));
      }

      goToStep(step) {
        this.currentStep = step;

        // Update step visibility
        this.steps.forEach((stepEl) => {
          const stepNum = parseInt(stepEl.dataset.step, 10);
          stepEl.classList.toggle('active', stepNum === step);
        });

        // Update progress
        this.updateProgress();

        // Focus email field on step 2
        if (step === 2 && this.recipientEmail) {
          setTimeout(() => this.recipientEmail.focus(), 100);
        }
      }

      updateProgress() {
        const isComplete = this.buyForMyself || this.currentStep === 2;

        if (this.progressFill) {
          this.progressFill.classList.toggle('complete', isComplete);
        }

        if (this.progressStep) {
          if (this.buyForMyself) {
            this.progressStep.textContent = 'Ready to add';
          } else {
            this.progressStep.textContent = `Step ${this.currentStep} of 2`;
          }
        }

        if (this.progressLabel) {
          this.progressLabel.textContent = this.currentStep === 1 ? 'Choose Amount' : 'Recipient Details';
        }
      }

      bindFormFields() {
        // Message character count
        this.recipientMessage?.addEventListener('input', () => {
          if (this.messageCount) {
            this.messageCount.textContent = this.recipientMessage.value.length;
          }
        });

        // Date validation on change
        this.recipientSendDate?.addEventListener('change', () => this.validateDate());

        // Clear errors on input
        this.recipientEmail?.addEventListener('input', () => {
          this.recipientEmail.classList.remove('error');
          if (this.errorEmail) {
            this.errorEmail.textContent = '';
            this.errorEmail.classList.remove('visible');
          }
        });
      }

      bindFormSubmit() {
        this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
      }

      handleSubmit(e) {
        // If buying for self, no recipient validation needed
        if (this.buyForMyself) {
          // Clear the send to recipient field
          if (this.sendToRecipientInput) {
            this.sendToRecipientInput.value = '';
          }
          return true;
        }

        // Validate recipient details
        let isValid = true;

        // Validate email
        if (!this.recipientEmail?.value || this.recipientEmail.value.trim() === '') {
          this.showError(this.errorEmail, this.recipientEmail, "Please enter the recipient's email address");
          isValid = false;
        } else if (!this.validateEmail(this.recipientEmail.value)) {
          this.showError(this.errorEmail, this.recipientEmail, 'Please enter a valid email address');
          isValid = false;
        }

        // Validate date
        if (!this.validateDate()) {
          isValid = false;
        }

        if (!isValid) {
          e.preventDefault();
          return false;
        }

        return true;
      }

      validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
      }

      validateDate() {
        if (!this.recipientSendDate?.value) return true;

        const selectedDate = new Date(this.recipientSendDate.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 90);
        maxDate.setHours(23, 59, 59, 999);

        if (selectedDate < today) {
          this.showError(this.errorDate, this.recipientSendDate, 'Send date cannot be in the past');
          return false;
        }

        if (selectedDate > maxDate) {
          this.showError(this.errorDate, this.recipientSendDate, 'Send date cannot be more than 90 days in the future');
          return false;
        }

        // Clear error
        if (this.errorDate) {
          this.errorDate.textContent = '';
          this.errorDate.classList.remove('visible');
        }
        this.recipientSendDate?.classList.remove('error');

        return true;
      }

      showError(errorEl, inputEl, message) {
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.classList.add('visible');
        }
        inputEl?.classList.add('error');
      }
    }
  );
}
